<div class="tw-flex tw-min-w-[320px] tw-flex-col tw-gap-4">
  @if (showInvalidRowsScreen) {
    <div
      #invalidRowsScreen
      appModalOverlay
      [appModalOverlayLabel]="'invalidStatementRowsTitle' | i18n"
      (modalOverlayDismiss)="closeInvalidRowsScreen()"
      class="mobile-slide-in-page tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-flex-col tw-bg-white"
    >
      <div
        class="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-dt-neutral-300 tw-p-4"
      >
        <h2 class="tw-m-0 tw-text-lg tw-font-semibold">{{ "invalidStatementRowsTitle" | i18n }}</h2>
        <button
          type="button"
          class="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-text-white"
          (click)="closeInvalidRowsScreen()"
        >
          {{ "done" | i18n }}
        </button>
      </div>
      <div appGlossScroll class="tw-flex-1 tw-overflow-auto tw-p-4">
        <p class="tw-mb-3 tw-text-sm tw-text-dt-neutral-700">
          {{ "invalidStatementRowsMobileDescription" | i18n }}
        </p>
        <div class="tw-flex tw-flex-col tw-gap-3">
          @for (row of invalidRows; track row.sourceRowIndex) {
            <div class="tw-rounded-lg tw-border tw-border-[#dc2626] tw-bg-[#fef2f2] tw-p-3">
              <div class="tw-mb-2 tw-text-sm tw-font-semibold tw-text-[#991b1b]">
                {{ "row" | i18n }} {{ row.sourceRowNumber }}
              </div>
              <dl class="tw-m-0 tw-grid tw-grid-cols-[88px_1fr] tw-gap-x-3 tw-gap-y-1 tw-text-sm">
                <dt class="tw-text-dt-neutral-600">{{ "date" | i18n }}</dt>
                <dd class="tw-m-0 tw-break-words">{{ row.rawDate || ("none" | i18n) }}</dd>
                <dt class="tw-text-dt-neutral-600">{{ "description" | i18n }}</dt>
                <dd class="tw-m-0 tw-break-words">{{ row.description }}</dd>
                <dt class="tw-text-dt-neutral-600">{{ "amount" | i18n }}</dt>
                <dd class="tw-m-0">{{ row.amount }}</dd>
              </dl>
            </div>
          }
        </div>
      </div>
    </div>
  }

  @if (isLoading()) {
    <p class="tw-py-6 tw-text-center tw-text-dt-neutral-700">{{ "loading" | i18n }}</p>
  } @else {
    <div class="tw-flex tw-gap-6">
      <!-- ===================== STATEMENT PREVIEW (hidden on xs) ===================== -->
      @if (grid.rows.length) {
        <div class="tw-flex tw-min-w-[360px] tw-flex-1 tw-flex-col tw-gap-2 xs:tw-hidden">
          @if (invalidRows.length) {
            <div class="tw-rounded-lg tw-border tw-border-[#dc2626] tw-bg-[#fef2f2] tw-p-2">
              <p class="tw-m-0 tw-text-xs tw-font-medium tw-text-[#991b1b]">
                {{ "invalidStatementRowsPreviewHint" | i18n }}
              </p>
            </div>
          }
          <div
            appGlossScroll
            class="tw-block tw-max-h-[70vh] tw-overflow-auto tw-rounded-lg tw-border tw-border-dt-neutral-300"
          >
            <table class="tw-w-full tw-border-collapse tw-text-xs">
              <thead class="tw-sticky tw-top-0 tw-bg-dt-neutral-200">
                <tr>
                  <th
                    class="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-dt-neutral-300 tw-p-2 tw-text-right tw-font-medium"
                  >
                    #
                  </th>
                  @for (header of grid.headers; track $index) {
                    <th
                      class="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-dt-neutral-300 tw-p-2 tw-text-left tw-font-medium"
                    >
                      {{ header }}
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (previewRow of previewGridRows; track previewRow.index) {
                  <tr
                    #previewRowElement
                    [attr.data-row-index]="previewRow.index"
                    class="tw-border-0 tw-border-b tw-border-dt-neutral-200"
                    [ngClass]="{
                      'tw-bg-[#fef2f2] tw-outline tw-outline-2 tw-outline-[#dc2626]':
                        isInvalidPreviewRow(previewRow.index),
                    }"
                  >
                    <td class="tw-whitespace-nowrap tw-p-2 tw-text-right tw-text-dt-neutral-600">
                      {{ previewRow.index + 2 }}
                    </td>
                    @for (header of grid.headers; track $index) {
                      <td
                        class="tw-whitespace-nowrap tw-p-2"
                        [ngClass]="
                          isInvalidPreviewRow(previewRow.index)
                            ? 'tw-text-[#991b1b]'
                            : 'tw-text-dt-neutral-700'
                        "
                      >
                        {{ previewRow.cells[$index] }}
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (isPreviewShifted) {
            <p class="tw-m-0 tw-text-xs tw-text-dt-neutral-600">
              {{
                "previewShowingRows"
                  | i18n: previewStartIndex + 2 : previewShowingEnd + 1 : grid.rows.length + 1
              }}
            </p>
          }
        </div>
      }

      <!-- ===================== MAP STEP ===================== -->
      <div class="tw-flex tw-flex-col tw-gap-4">
        <p class="tw-text-sm tw-text-dt-neutral-700">
          {{ "mapStatementColumnsDescription" | i18n }}
        </p>

        <!-- 1. Account -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "account" | i18n }}</label>
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="selectedAccountId"
            (ngModelChange)="clearInvalidRows()"
          >
            @for (account of accounts; track account.id) {
              <option [ngValue]="account.id">{{ account.displayName }}</option>
            }
          </select>
          <button
            type="button"
            class="tw-self-start tw-text-sm tw-text-primary tw-underline"
            (click)="createAccount()"
          >
            {{ "createNewAccount" | i18n }}
          </button>
        </div>

        <!-- 2. Statement currency -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "statementCurrency" | i18n }}</label>
          <input
            type="text"
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [value]="selectedAccountCurrency"
            disabled
          />
        </div>

        <!-- 3. Amount: single signed column OR debit/credit pair -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "amount" | i18n }}</label>
          <div class="tw-flex tw-gap-4 tw-text-sm">
            <label class="tw-flex tw-items-center tw-gap-1">
              <input
                type="radio"
                name="amountMode"
                value="single"
                [(ngModel)]="amountMode"
                (ngModelChange)="clearInvalidRows()"
              />
              {{ "singleAmountColumn" | i18n }}
            </label>
            <label class="tw-flex tw-items-center tw-gap-1">
              <input
                type="radio"
                name="amountMode"
                value="split"
                [(ngModel)]="amountMode"
                (ngModelChange)="clearInvalidRows()"
              />
              {{ "debitCreditColumns" | i18n }}
            </label>
          </div>
          @if (amountMode === "single") {
            <select
              class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
              [(ngModel)]="amountIndex"
              (ngModelChange)="clearInvalidRows()"
            >
              @for (header of headers; track $index) {
                <option [ngValue]="$index">{{ header }}</option>
              }
            </select>
          } @else {
            <div class="tw-flex tw-gap-2">
              <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                <label class="tw-text-xs tw-text-dt-neutral-600">{{
                  "debitColumnOut" | i18n
                }}</label>
                <select
                  class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
                  [(ngModel)]="debitIndex"
                  (ngModelChange)="clearInvalidRows()"
                >
                  <option [ngValue]="-1">{{ "none" | i18n }}</option>
                  @for (header of headers; track $index) {
                    <option [ngValue]="$index">{{ header }}</option>
                  }
                </select>
              </div>
              <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                <label class="tw-text-xs tw-text-dt-neutral-600">{{
                  "creditColumnIn" | i18n
                }}</label>
                <select
                  class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
                  [(ngModel)]="creditIndex"
                  (ngModelChange)="clearInvalidRows()"
                >
                  <option [ngValue]="-1">{{ "none" | i18n }}</option>
                  @for (header of headers; track $index) {
                    <option [ngValue]="$index">{{ header }}</option>
                  }
                </select>
              </div>
            </div>
          }
        </div>

        <!-- 4. Date column + format -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "date" | i18n }}</label>
          <!-- Always editable: auto-detection only prefills the column; a wrong guess
               (e.g. "Value Date" before "Posted Date") must be overridable. -->
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="dateIndex"
            (ngModelChange)="onDateColumnChange()"
          >
            @for (header of headers; track $index) {
              <option [ngValue]="$index">{{ header }}</option>
            }
          </select>
          @if (dateAutoDetected) {
            <p class="tw-text-xs tw-text-dt-neutral-600">
              {{ "dateColumnDetected" | i18n }}:
              <span class="tw-font-medium">{{ headers[dateIndex] }}</span>
            </p>
          }
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="dateFormat"
            (ngModelChange)="clearInvalidRows()"
          >
            @for (fmt of dateFormats; track fmt) {
              <option [ngValue]="fmt">{{ fmt }}</option>
            }
          </select>
        </div>

        <!-- 5. Description column -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "description" | i18n }}</label>
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="descriptionIndex"
            (ngModelChange)="clearInvalidRows()"
          >
            @for (header of headers; track $index) {
              <option [ngValue]="$index">{{ header }}</option>
            }
          </select>
        </div>

        <!-- 6. Category column (optional) -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "category" | i18n }}</label>
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="categoryIndex"
            (ngModelChange)="clearInvalidRows()"
          >
            <option [ngValue]="-1">{{ "none" | i18n }}</option>
            @for (header of headers; track $index) {
              <option [ngValue]="$index">{{ header }}</option>
            }
          </select>
        </div>

        <!-- 7. Balance column (optional, link-account only) -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <label class="tw-text-sm tw-font-medium">{{ "balanceColumnOptional" | i18n }}</label>
          <select
            class="tw-rounded-lg tw-border tw-border-dt-neutral-400 tw-p-2"
            [(ngModel)]="balanceIndex"
            (ngModelChange)="clearInvalidRows()"
          >
            <option [ngValue]="-1">{{ "none" | i18n }}</option>
            @for (header of headers; track $index) {
              <option [ngValue]="$index">{{ header }}</option>
            }
          </select>
        </div>

        <!-- Type: auto-derived from amount sign -->
        <p class="tw-rounded-lg tw-bg-dt-neutral-200 tw-p-2 tw-text-xs tw-text-dt-neutral-700">
          {{ "typeAutoFromAmountHint" | i18n }}
        </p>

        <div class="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
          <button
            type="button"
            class="tw-rounded-xl tw-bg-primary tw-px-6 tw-py-2 tw-text-white"
            [disabled]="isSaving()"
            (click)="map()"
          >
            {{ isSaving() ? ("loading" | i18n) : ("map" | i18n) }}
          </button>
        </div>
      </div>
    </div>
  }
</div>
